import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useState, type ReactNode } from 'react';
import { useSoleEntity } from '@/api/entities';

/** The filter key every list page uses for its entity dropdown. */
const ENTITY_FILTER_KEY = 'entity_id';

export type FilterOption = { value: string; label: string };
export type FilterDef = {
  key: string;
  label: string;
  options: FilterOption[];
  /** Show a "(all)" option at the top. Defaults to true. */
  allowAll?: boolean;
  /** Min width in px. Defaults to 150. */
  minWidth?: number;
};

type Props = {
  /** Search box placeholder. Pass empty string to hide the search box entirely. */
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (v: string) => void;
  /** Debounce in ms. Defaults to 350. */
  debounceMs?: number;
  /**
   * Show an explicit Search button beside the box, and stop searching as you
   * type. Opt-in: the debounced-as-you-type default is better for small lists,
   * but on a large server-backed table an explicit submit avoids firing a
   * query for every prefix of what someone is typing.
   */
  searchButton?: boolean;

  filters?: FilterDef[];
  values: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;

  /** Slot for extras (e.g. count label, "Add" button). */
  rightSlot?: ReactNode;

  /** Show a Clear button when any filter is active. */
  onClearAll?: () => void;
};

/**
 * Standard filter strip — search input + dropdown filters + a clear button +
 * a free slot for counts/extras on the right. Used by every list page.
 *
 * Internally debounces the search input so typing doesn't fire one query
 * per keystroke. Filter dropdowns fire onFilterChange synchronously.
 */
export function FilterBar({
  searchPlaceholder = 'Search…',
  searchValue,
  onSearchChange,
  debounceMs = 350,
  searchButton = false,
  filters = [],
  values,
  onFilterChange,
  rightSlot,
  onClearAll,
}: Props) {
  // Local mirror of the search input — propagates after debounce so the
  // upstream query doesn't refire on every keystroke.
  const [localSearch, setLocalSearch] = useState(searchValue);
  useEffect(() => {
    if (searchButton || localSearch === searchValue) return;
    const t = setTimeout(() => onSearchChange(localSearch), debounceMs);
    return () => clearTimeout(t);
  }, [localSearch]);  // eslint-disable-line react-hooks/exhaustive-deps

  const submitSearch = () => onSearchChange(localSearch);

  // Re-sync if parent clears externally
  useEffect(() => {
    if (searchValue !== localSearch) setLocalSearch(searchValue);
  }, [searchValue]);  // eslint-disable-line react-hooks/exhaustive-deps

  // One entity in the workspace: the `entity_id` filter has one answer, so it
  // is not shown. "All" already means that entity; a filter that has no
  // "All" gets the entity written in so the page still has its scope.
  const sole = useSoleEntity();
  const isHidden = (f: FilterDef) => !!sole && f.key === ENTITY_FILTER_KEY;
  const entityFilter = filters.find((f) => f.key === ENTITY_FILTER_KEY);
  const needsSole = !!sole && !!entityFilter && entityFilter.allowAll === false
    && values[ENTITY_FILTER_KEY] !== sole.id;
  useEffect(() => {
    if (needsSole && sole) onFilterChange(ENTITY_FILTER_KEY, sole.id);
  }, [needsSole, sole?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const anyActive =
    !!searchValue ||
    Object.entries(values).some(([k, v]) => !!v && !(sole && k === ENTITY_FILTER_KEY));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 2,
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {searchPlaceholder && (
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => { if (searchButton && e.key === 'Enter') submitSearch(); }}
          sx={{ minWidth: 240, flex: '1 0 240px' }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
              endAdornment: localSearch ? (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => { setLocalSearch(''); onSearchChange(''); }}
                    sx={{ minWidth: 'auto', p: 0.5 }} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      )}

      {searchPlaceholder && searchButton && (
        <Button
          variant="contained" size="medium" onClick={submitSearch}
          startIcon={<SearchIcon fontSize="small" />}
          sx={{ height: 40, px: 2.5, flexShrink: 0 }}
        >
          Search
        </Button>
      )}

      {filters.filter((f) => !isHidden(f)).map((f) => {
        // Defensive guard: if the parent has a saved value (e.g. a UUID
        // entity_id) but the options haven't loaded yet — happens during
        // page mount before the entities query resolves — MUI warns
        // "out-of-range value … available values are ``". Render only the
        // safe value to MUI and keep the actual value in the parent state.
        const rawValue = values[f.key] ?? '';
        const optionValues = new Set(f.options.map((o) => o.value));
        const safeValue = rawValue === '' || optionValues.has(rawValue) ? rawValue : '';
        return (
          <FormControl key={f.key} size="small" sx={{ minWidth: f.minWidth ?? 150 }}>
            <InputLabel>{f.label}</InputLabel>
            <Select
              label={f.label}
              value={safeValue}
              onChange={(e) => onFilterChange(f.key, e.target.value || '')}
            >
              {(f.allowAll ?? true) && (
                <MenuItem value="">All</MenuItem>
              )}
              {f.options.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      })}

      {anyActive && onClearAll && (
        <Button size="small" startIcon={<ClearIcon />} onClick={onClearAll} color="inherit">
          Clear
        </Button>
      )}

      <Box sx={{ flex: 1 }} />
      {rightSlot}
    </Paper>
  );
}
